import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  DpPayrollItemKind,
  DpPayrollResultDetail,
  getPayrollResultApi,
  PAYROLL_RESULT_STATUS_BADGE,
  PAYROLL_RESULT_STATUS_LABELS,
} from "../api/payrollApi";
import { formatCpf, formatCurrency } from "../utils/format";
import { DP_EMPLOYEE_TAB_PAGE, DP_PAYROLL_PERIOD_PAGE, DP_PAGE, DP_PAYROLL_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const SECTIONS: Array<{ kind: DpPayrollItemKind; title: string }> = [
  { kind: "PROVENTO", title: "Proventos" },
  { kind: "DESCONTO", title: "Descontos" },
  { kind: "INFORMATIVO", title: "Informativos (não entram no líquido)" },
];

const formatReference = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

export default function PayrollResultPage() {
  const { id = "", contractId = "" } = useParams<{ id: string; contractId: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAYROLL_PERIOD_PAGE(id));
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [result, setResult] = useState<DpPayrollResultDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !id || !contractId) return;
    getPayrollResultApi(token, id, contractId)
      .then(setResult)
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar o resultado."))
      .finally(() => setLoading(false));
  }, [token, id, contractId]);

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          {loading ? (
            <div className="dpEmpty">Carregando...</div>
          ) : !result ? (
            <div className="dpEmpty">Resultado não encontrado. Calcule a competência.</div>
          ) : (
            <>
              <EcoPageHeader
                crumbs={[
                  { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
                  { label: "Folha", onClick: () => navigate(DP_PAYROLL_PAGE()) },
                  { label: "Competência", onClick: goBack },
                  { label: result.meta.fullName },
                ]}
                title={result.meta.fullName}
                subtitle={`${result.meta.registrationNumber ? `Matrícula ${result.meta.registrationNumber} · ` : ""}CPF ${formatCpf(result.meta.cpf)}${
                  result.meta.costCenter ? ` · ${result.meta.costCenter.code} - ${result.meta.costCenter.name}` : ""
                }${result.meta.store ? ` · ${result.meta.store}` : ""}`}
                onBack={goBack}
                backLabel="Voltar para a competência"
                actions={
                  <>
                    <span className={`dpBadge ${PAYROLL_RESULT_STATUS_BADGE[result.status]} dpeHeaderBadge`}>
                      {PAYROLL_RESULT_STATUS_LABELS[result.status]}
                    </span>
                    <button
                      type="button"
                      className="ecoBtn"
                      onClick={() => navigate(DP_EMPLOYEE_TAB_PAGE(result.employeeId, "contrato"))}
                    >
                      <EcoIcon name="users" size={18} />
                      Ver ficha
                    </button>
                  </>
                }
              />

              {result.status === "SEM_PONTO" ? (
                <div className="dpNotice dpNotice--warning">
                  <strong>Sem apuração do mês.</strong> Os valores deste colaborador foram calculados como mês cheio,
                  sem horas extras nem faltas, e por isso não são exibidos. Na competência, clique em{" "}
                  <strong>Apurar mês</strong> para confirmar ou corrigir os totais e recalcule.
                </div>
              ) : null}

              {result.warnings.length && result.status !== "SEM_PONTO" ? (
                <div className={`dpNotice ${result.status === "PENDENTE" ? "dpNotice--warning" : ""}`}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {result.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {result.status === "SEM_PONTO" ? null : (
              <>
              <div className="dpCounters">
                {[
                  ["Bruto", formatCurrency(result.grossAmount)],
                  ["Descontos (com adiantamento)", formatCurrency(result.deductionsAmount)],
                  ["Adiantamento (já pago)", formatCurrency(result.advanceAmount)],
                  ["Líquido final", formatCurrency(result.netAmount)],
                ].map(([label, value]) => (
                  <div key={label} className="dpCounter">
                    <div className="dpCounter__value">{value}</div>
                    <div className="dpCounter__label">{label}</div>
                  </div>
                ))}
              </div>

              <section className="dpSection">
                <div className="dpDefinitionGrid">
                  {[
                    ["Base do INSS", result.inssBase],
                    ["Base do IRRF (após deduções)", result.irrfBase],
                    ["Base do FGTS", result.fgtsBase],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div className="dpDefinition__label">{label}</div>
                      <div className="dpDefinition__value">{formatCurrency(value)}</div>
                    </div>
                  ))}
                </div>
              </section>

              {SECTIONS.map(({ kind, title }) => {
                const lines = result.lines.filter((l) => l.kind === kind);
                if (lines.length === 0) return null;
                return (
                  <section key={kind} className="dpSection">
                    <h2 className="dpSection__title">{title}</h2>
                    <div className="dpTableWrapper">
                      <table className="dpTable">
                        <thead>
                          <tr>
                            <th>Rubrica</th>
                            <th>Referência</th>
                            <th>Valor</th>
                            <th>Como foi calculado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((l) => (
                            <tr key={l.id}>
                              <td>
                                <strong>{l.itemName}</strong>
                                {l.externalCode ? <div className="dpFormHint">Código {l.externalCode}</div> : null}
                              </td>
                              <td>{formatReference(l.reference)}</td>
                              <td>{formatCurrency(l.amount)}</td>
                              <td className="dpFormHint" style={{ whiteSpace: "normal" }}>
                                {l.memo}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
              </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
